/**
 * The shared Syntry toast.
 *
 * This is the ONLY place in the app that may call `toastController`. Every
 * toast — success, error, validation refusal, plain notice — goes through here
 * so that the icon, palette, sizing, bottom clearance, enter/leave motion and
 * dwell time are decided once instead of 31 times.
 *
 * Presentation lives in `src/theme/toast.scss`; the `syntry-toast*` classes
 * built below are the contract between the two files.
 *
 * Two presets over one implementation: `useToast()` is silent — what every
 * existing call site gets, unchanged — and `useNotify()` additionally plays the
 * variant's sound (`services/notification-sound.ts`), for an outcome the user
 * just caused and is waiting on. Either can be overridden per call with
 * `{ sound: true | false }`.
 *
 * Callable from plain modules as well as components: it is a frozen
 * module-level singleton with no reactive state and no lifecycle hooks, which
 * matters because several callers (`src/pwa/offline-ui.ts`,
 * `src/composables/useClockActions.ts`) have no component instance.
 */
import { createAnimation, toastController } from '@ionic/vue';
import type { Animation, AnimationBuilder } from '@ionic/vue';
import { alertCircle, checkmarkCircle, closeCircle, informationCircle } from 'ionicons/icons';
import router from '@/router';
import type { AppSoundKind } from '@/config/notifications';
import { playAppSound } from '@/services/notification-sound';
import { isComposerRoute, isTabBarVisible } from '@/utils/tab-bar';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'neutral';

export interface ToastOverrides {
  /** Bold accent-coloured first line. Omit for the plain one-line shape. */
  title?: string;
  /** Override the variant's dwell time, in ms. `0` stays until dismissed. */
  duration?: number;
  /** Defaults to 'bottom'. Use 'top' only when something owns the bottom edge. */
  position?: 'top' | 'bottom';
  /** One trailing action. Tapping it dismisses the toast. */
  action?: { text: string; handler?: () => void };
  /**
   * Play the variant's sound as well as showing the card.
   *
   * Defaults to the preset: `useToast()` is silent, `useNotify()` sounds. Pass
   * it explicitly to go against the preset at one call site.
   */
  sound?: boolean;
}

export interface UseToast {
  /** Brand-tinted, makes no status claim. The default for plain notices. */
  show(message: string, overrides?: ToastOverrides): Promise<void>;
  success(message: string, overrides?: ToastOverrides): Promise<void>;
  error(message: string, overrides?: ToastOverrides): Promise<void>;
  warning(message: string, overrides?: ToastOverrides): Promise<void>;
  info(message: string, overrides?: ToastOverrides): Promise<void>;
  /** Variant chosen at runtime — for `ok ? … : …` call sites. */
  toast(variant: ToastVariant, message: string, overrides?: ToastOverrides): Promise<void>;
  /** Dismiss whatever is currently up. */
  dismiss(): Promise<void>;
}

/**
 * Per-variant dwell time. ERPat's web toasts run 4/5/6/7s, but those sit in a
 * corner; ours covers the bottom of the content it is talking about, so the
 * scale is shorter.
 */
const DURATION_MS: Record<ToastVariant, number> = {
  neutral: 3000,
  success: 3000,
  info: 4000,
  warning: 4500,
  error: 5000,
};

/**
 * All four are FILLED-CIRCLE glyphs on purpose: the glyph itself is the accent
 * chip. A real chip behind the icon is not buildable from outside anyway —
 * `ion-icon` pins `box-sizing: content-box !important` on its own `:host`, and
 * an inner-tree `!important` beats any outer-tree `::part()` declaration.
 *
 * `alertCircle` for warning rather than `warning`: the triangle would be the
 * only non-circular mark in the set.
 */
const ICON: Record<ToastVariant, string> = {
  neutral: informationCircle,
  success: checkmarkCircle,
  error: closeCircle,
  warning: alertCircle,
  info: informationCircle,
};

/**
 * Which clip each variant sounds with, when sound is on.
 *
 * Three clips for five variants: `warning` borrows the error cue (both mean
 * "that did not go through"), `info`/`neutral` borrow the arrival chime. Adding
 * a fourth sound would be two more clips to precache for a distinction nobody
 * can hear in 1.2 seconds.
 */
const SOUND: Record<ToastVariant, AppSoundKind> = {
  neutral: 'notification',
  success: 'success',
  error: 'error',
  warning: 'error',
  info: 'notification',
};

const ENTER_MS = 260;
const LEAVE_MS = 220;
const ENTER_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const LEAVE_EASING = 'cubic-bezier(0.4, 0, 1, 1)';
const ENTER_TRAVEL_PX = 14;
const LEAVE_TRAVEL_PX = 10;

/**
 * One toast at a time. Ionic queues nothing, and two 60px cards stacked on the
 * bottom edge is worse than losing the older message.
 */
let current: HTMLIonToastElement | null = null;

/** Re-read per toast, never cached — the OS setting can change mid-session. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * The node Ionic's own toast animations move.
 *
 * The `?? baseEl` arm is a strict-mode fallback only: Stencil has rendered the
 * wrapper before `present()` runs the builder, which is why Ionic's own
 * builders query it unguarded.
 */
function wrapperOf(baseEl: HTMLElement): Element {
  return baseEl.shadowRoot?.querySelector('.toast-wrapper') ?? baseEl;
}

/**
 * The `opts` Ionic hands an `ion-toast` animation builder.
 *
 * `top`/`bottom` are CSS length STRINGS carrying the resting offset Ionic
 * computed for this toast, safe-area inset included — e.g.
 * `calc(-10px - var(--ion-safe-area-bottom, 0px))`. Not exported by
 * `@ionic/vue`, so it is redeclared here.
 */
interface ToastAnimationOpts {
  position: 'top' | 'bottom' | 'middle';
  top: string;
  bottom: string;
}

/**
 * Enter: rise, fade, and a hair of scale. 260ms with no overshoot — Ionic's iOS
 * default is a 400ms spring, too bouncy for a card this size.
 *
 * `${rest}` is Ionic's computed resting offset and MUST appear on BOTH
 * keyframes: Ionic calls `animation.fill('both')`, so the final keyframe is
 * what holds the toast on screen. Drop it and the wrapper falls back to its CSS
 * `translate3d(0, 100%, 0)` — offscreen.
 *
 * The travel is stacked as a SECOND `translateY()` rather than folded into
 * Ionic's `calc()` string so both keyframes carry the same transform function
 * list and the Web Animations API can interpolate them.
 */
const toastEnter: AnimationBuilder = (
  baseEl: HTMLElement,
  opts: ToastAnimationOpts,
): Animation => {
  const rest = opts.position === 'top' ? opts.top : opts.bottom;
  const travel = opts.position === 'top' ? -ENTER_TRAVEL_PX : ENTER_TRAVEL_PX;
  return createAnimation()
    .addElement(wrapperOf(baseEl))
    .duration(ENTER_MS)
    .easing(ENTER_EASING)
    .fromTo('opacity', '0', '1')
    .fromTo(
      'transform',
      `translateY(${rest}) translateY(${travel}px) scale(0.96)`,
      `translateY(${rest}) translateY(0px) scale(1)`,
    );
};

/**
 * Leave: settle down, fade, shrink. 220ms on a sharp ease-in — the "gone" curve.
 *
 * ERPat exits with `translateX(24px)`, which reads correctly for its
 * right-anchored stacked cards. Ours is bottom-anchored and near-full-width, so
 * sliding right would pull the left edge off its inset while the right edge
 * crossed the screen.
 */
const toastLeave: AnimationBuilder = (
  baseEl: HTMLElement,
  opts: ToastAnimationOpts,
): Animation => {
  const rest = opts.position === 'top' ? opts.top : opts.bottom;
  const travel = opts.position === 'top' ? -LEAVE_TRAVEL_PX : LEAVE_TRAVEL_PX;
  return createAnimation()
    .addElement(wrapperOf(baseEl))
    .duration(LEAVE_MS)
    .easing(LEAVE_EASING)
    .fromTo('opacity', '1', '0')
    .fromTo(
      'transform',
      `translateY(${rest}) translateY(0px) scale(1)`,
      `translateY(${rest}) translateY(${travel}px) scale(0.94)`,
    );
};

/**
 * Which fixed furniture this toast has to clear.
 *
 * Read off the router singleton rather than `useRoute()`: half the callers are
 * plain modules with no component instance. `src/router/index.ts` lazy-imports
 * every view, so this import cannot form a module cycle.
 */
function edgeClasses(position: 'top' | 'bottom'): string[] {
  if (position === 'top') return ['syntry-toast--top'];
  // A presented modal covers the tab bar, so clearing it would float the toast
  // 64px above nothing.
  if (document.querySelector('ion-modal.show-modal') !== null) return [];
  const path = router.currentRoute.value.path;
  if (isComposerRoute(path)) return ['syntry-toast--over-composer'];
  return isTabBarVisible(path) ? ['syntry-toast--over-tabs'] : [];
}

async function present(
  variant: ToastVariant,
  message: string,
  overrides: ToastOverrides = {},
  soundByDefault = false,
): Promise<void> {
  const position = overrides.position ?? 'bottom';
  const duration = overrides.duration ?? DURATION_MS[variant];
  const reduced = prefersReducedMotion();

  // Before the await, so the cue lands with the card rather than after it.
  // `playAppSound` is total by contract and silent until the autoplay unlock has
  // run; the try is belt-and-braces, because the message is the half that
  // matters and must never be lost to the half that decorates it.
  if (overrides.sound ?? soundByDefault) {
    try {
      playAppSound(SOUND[variant]);
    } catch {
      /* a sound is never worth a swallowed message */
    }
  }

  const cssClass = ['syntry-toast', `syntry-toast--${variant}`, ...edgeClasses(position)];
  if (overrides.title !== undefined) cssClass.push('syntry-toast--titled');
  if (duration <= 0) cssClass.push('syntry-toast--sticky');

  const el = await toastController.create({
    message,
    ...(overrides.title !== undefined ? { header: overrides.title } : {}),
    icon: ICON[variant],
    cssClass,
    duration,
    position,
    // Dismissal without a close button: Ionic skips the leave animation for a
    // gesture role, so there is no snap-back to fight.
    swipeGesture: 'vertical',
    // How prefers-reduced-motion is honoured. `overlays.js` zeroes the
    // animation duration when `animated` is false, and `fill('both')` then
    // applies the final keyframe instantly. Neither global.scss's `*` guard nor
    // a media query can reach a Web Animations overlay transition.
    animated: !reduced,
    enterAnimation: toastEnter,
    leaveAnimation: toastLeave,
    // Ionic's own `color` is deliberately never passed: `:host(.ion-color)
    // .toast-wrapper` sets `background` directly, which would make the tinted
    // `--background` in toast.scss inert. Variants ride on cssClass instead.
    ...(overrides.action
      ? {
          buttons: [
            { text: overrides.action.text, role: 'cancel', handler: overrides.action.handler },
          ],
        }
      : {}),
  });

  // Countdown-bar timing, read by `::part(wrapper)::after` in toast.scss. This
  // CANNOT ride on `ToastOptions.htmlAttributes`: ion-toast's render spreads
  // htmlAttributes and THEN overwrites `style` with its own z-index object.
  // Stencil's patcher only ever touches zIndex, so these survive re-render.
  el.style.setProperty('--syntry-toast-duration', `${duration}ms`);
  el.style.setProperty('--syntry-toast-enter', `${reduced ? 0 : ENTER_MS}ms`);

  const previous = current;
  current = el;
  // Fire-and-forget so the old card fades out while the new one fades in.
  // Awaiting would add the full 220ms leave to every toast.
  void previous?.dismiss();
  void el.onDidDismiss().then(() => {
    if (current === el) current = null;
  });

  await el.present();
}

/**
 * Both presets are the same object shape over the same `present()`; they differ
 * only in whether sound is on when the call site says nothing.
 */
function createPreset(soundByDefault: boolean): UseToast {
  return Object.freeze({
    show: (message: string, overrides?: ToastOverrides) =>
      present('neutral', message, overrides, soundByDefault),
    success: (message: string, overrides?: ToastOverrides) =>
      present('success', message, overrides, soundByDefault),
    error: (message: string, overrides?: ToastOverrides) =>
      present('error', message, overrides, soundByDefault),
    warning: (message: string, overrides?: ToastOverrides) =>
      present('warning', message, overrides, soundByDefault),
    info: (message: string, overrides?: ToastOverrides) =>
      present('info', message, overrides, soundByDefault),
    toast: (variant: ToastVariant, message: string, overrides?: ToastOverrides) =>
      present(variant, message, overrides, soundByDefault),
    dismiss: async (): Promise<void> => {
      await current?.dismiss();
    },
  });
}

const silent = createPreset(false);
const audible = createPreset(true);

/** The toast, silent. What every existing call site uses. */
export function useToast(): UseToast {
  return silent;
}

/**
 * The toast, with sound — for an outcome the user just caused and is waiting on.
 *
 *     const notify = useNotify();
 *     notify.success('Leave request submitted');   // green card + success.mp3
 *     notify.error('Could not submit');            // red card + error.mp3
 *
 * Identical to {@link useToast} in every other respect, including the one-at-a-time
 * rule — the two presets share the singleton toast, so they can be mixed freely
 * in one screen.
 *
 * Reach for it when the user pressed something and is waiting for a verdict
 * (save, submit, clock in/out, delete). NOT for background news — a realtime
 * arrival already chimes through `presentToast()`, and a second cue for the same
 * event is noise. NOT for a passive notice either: `notify.show()` works, but a
 * message nobody asked for rarely earns a sound.
 */
export function useNotify(): UseToast {
  return audible;
}
